/* Downforce 3D theme — shared behaviour.
 *
 * ⭐ Deliberately small. The React app carried framer-motion, radix, lucide and react-router to do
 * what a server-rendered theme gets for free. The only thing genuinely worth keeping is the
 * scroll-reveal, because it is visible brand behaviour on every section of the current site.
 *
 * ⭐ 2026-09-19: gallery IIFE is the LIVE rev (dynamic mainsNow / thumbsNow + df3d:gallery-rebuilt
 * / df3d:gallery-show). Clarity CRO swipe + tap-to-advance sit on that base. Do not revert to the
 * boot-time mains snapshot — Circuit Hex starts with 0–1 photos and syncGallery injects more.
 * Hex STAGE (rev=6): in-card prev/next; no vertical scrollIntoView after Add;
 * Circuit Hex / circuit-hex-10-inch bottom gallery stays first-load static
 * (syncGallery still runs in configurator.js &rev=28 — this file restores);
 * strip Special instructions blocks on hex-list rebuild;
 * strip “Choose Your Edition” on preset Hex pages (rev=7);
 * header is NOT pinned (rev=8); hex-strip centers the active card.
 * Policy: docs/mobile-desktop-split.md
 * MOBILE-ONLY (rev=9): enhanceHexCards / centerCardX / strip tap-jump — isMobileHexUx().
 * DESKTOP (rev=10): Circuit Hex gallery follows track pick like live (skip freeze ≥1024).
 * OPEN (ungated, Josh undecided): stripNotes, edition strip.
 */
(function () {
  'use strict';

  /* ── scroll reveal ──────────────────────────────────────────────────────────────────────
   * Replaces framer-motion's `whileInView` entrances.
   *
   * ⛔ THE ELEMENTS START VISIBLE IN THE MARKUP. The hiding class is applied by THIS script,
   * immediately before the observer takes over. That ordering is deliberate: if JS never runs —
   * blocked, failed, ancient browser — the visitor sees a complete page rather than a blank one.
   * The React version had the opposite failure mode, and on a store about to buy paid traffic an
   * invisible page is an invisible product.
   */
  var revealables = document.querySelectorAll('[data-reveal]');

  if (!('IntersectionObserver' in window) || !revealables.length) return;

  // Respect the user's motion preference — reveal instantly, no transition.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('reveal-ready');
      entry.target.classList.add('reveal-in');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

  revealables.forEach(function (el) {
    // ⚠ Anything already on screen at load is never hidden at all — hiding then revealing content
    // the visitor is already looking at reads as a flicker, not an animation.
    var box = el.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) {
      el.classList.add('reveal-in');
      return;
    }
    el.classList.add('reveal-ready');
    observer.observe(el);
  });
})();

/* ── product gallery ──────────────────────────────────────────────────────────────────────────
 * Lives in theme.js, NOT configurator.js: the gallery is on every product page including the
 * desk stand, where the configurator deliberately does not render at all.
 *
 * ⛔ Query the slides on every show(). configurator.js syncGallery() replaces [data-gallery]
 * innerHTML and fires df3d:gallery-rebuilt. A boot-time NodeList goes stale; Circuit Hex would
 * look like it had no gallery after the first pick.
 * ⚠ Progressive enhancement — with JS off the markup already shows image 1.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-gallery]');
  if (!root) return;

  function mainsNow() {
    return [].slice.call(root.querySelectorAll('[data-gallery-main]'));
  }
  function thumbsNow() {
    return [].slice.call(root.querySelectorAll('[data-gallery-thumb]'));
  }

  var at = 0;

  function show(i) {
    var mains = mainsNow();
    var thumbs = thumbsNow();
    if (!mains.length) return;
    // ⛔ Wrap rather than clamp — arrows that stop dead at the ends read as broken on a 2-photo set.
    at = (i + mains.length) % mains.length;
    mains.forEach(function (img, n) { img.classList.toggle('hidden', n !== at); });
    thumbs.forEach(function (t, n) {
      t.classList.toggle('border-primary', n === at);
      t.classList.toggle('border-border', n !== at);
    });
    var counter = root.querySelector('[data-gallery-count]');
    if (counter) counter.textContent = (at + 1) + ' / ' + mains.length;
  }

  root.addEventListener('df3d:gallery-rebuilt', function () {
    at = 0;
    show(0);
    /* Hex STAGE redo: do not scroll this gallery into view after Add. Photo
     * browse lives on the hex-card arrows in the side-scroll strip. */
  });

  /* MOBILE-ONLY: Circuit Hex first-load freeze. DESKTOP ≥1024: leave
     configurator syncGallery in place so the right-side photos follow
     the selected track — same as live. configurator.js stays &rev=28. */
  function isDesktopGalleryLive() {
    return !!(window.matchMedia && window.matchMedia('(min-width: 1024px)').matches);
  }
  var cfg = document.getElementById('df3d-configurator');
  var hexHandle = cfg && cfg.getAttribute('data-product-handle');
  if (hexHandle === 'circuit-hex' || hexHandle === 'circuit-hex-10-inch') {
    var firstLoad = root.innerHTML;
    root.addEventListener('df3d:gallery-rebuilt', function () {
      if (isDesktopGalleryLive()) {
        root.removeAttribute('data-df3d-gallery-frozen');
        return;
      }
      var key = root.getAttribute('data-df3d-key');
      var set = root.getAttribute('data-df3d-set');
      root.innerHTML = firstLoad;
      if (key) root.setAttribute('data-df3d-key', key);
      if (set) root.setAttribute('data-df3d-set', set);
      root.setAttribute('data-df3d-gallery-frozen', '1');
      at = 0;
      show(0);
    });
  }
  root.addEventListener('df3d:gallery-show', function (e) {
    var i = e && e.detail ? e.detail.index : 0;
    if (typeof i === 'number' && i >= 0) show(i);
  });

  var touchX = 0;
  var touchY = 0;
  var swiped = false;

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-gallery-next]')) { e.preventDefault(); show(at + 1); return; }
    if (e.target.closest('[data-gallery-prev]')) { e.preventDefault(); show(at - 1); return; }
    var t = e.target.closest('[data-gallery-thumb]');
    if (t) { e.preventDefault(); show(parseInt(t.getAttribute('data-gallery-thumb'), 10) || 0); return; }
    /* CLARITY CRO: tap on the photo advances — people were already doing this; a dead main image
     * is the gallery half of the Road America / Sonoma dead-click callout. */
    if (!swiped && e.target.closest('[data-gallery-stage], [data-gallery-main]')) {
      if (e.target.closest('[data-gallery-next], [data-gallery-prev], [data-gallery-count]')) return;
      if (mainsNow().length < 2) return;
      e.preventDefault();
      show(at + 1);
    }
  });

  root.addEventListener('touchstart', function (e) {
    if (!e.changedTouches || !e.changedTouches[0]) return;
    if (e.target.closest('[data-gallery-thumb], [data-gallery-next], [data-gallery-prev]')) return;
    touchX = e.changedTouches[0].clientX;
    touchY = e.changedTouches[0].clientY;
    swiped = false;
  }, { passive: true });

  root.addEventListener('touchend', function (e) {
    if (!e.changedTouches || !e.changedTouches[0]) return;
    if (e.target.closest('[data-gallery-thumb], [data-gallery-next], [data-gallery-prev]')) return;
    var dx = e.changedTouches[0].clientX - touchX;
    var dy = e.changedTouches[0].clientY - touchY;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (mainsNow().length < 2) return;
    swiped = true;
    if (dx < 0) show(at + 1);
    else show(at - 1);
  }, { passive: true });

  /* ⭐ Arrow keys only once the gallery has been touched — binding them globally would hijack
   * arrow keys while someone is typing engraving text in the configurator below. */
  var engaged = false;
  root.addEventListener('click', function () { engaged = true; });
  document.addEventListener('keydown', function (e) {
    if (!engaged) return;
    var tag = (document.activeElement && document.activeElement.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.key === 'ArrowRight') show(at + 1);
    else if (e.key === 'ArrowLeft') show(at - 1);
  });
})();

/* ── hex-card photos + in-strip arrows (Hex STAGE, theme.js not configurator.js) ────────
 * MOBILE-ONLY (≤1023): rewrite src to 800 + prev/next chrome + centerCardX.
 * Desktop: leave stacked configurator thumbs (live-ish). unwrapHexStages on resize.
 * OPEN: stripNotes still runs at every width (SI hide undecided).
 * Configurator stays &rev=28.
 */
(function () {
  'use strict';
  var list = document.querySelector('[data-hex-list]');
  if (!list) return;

  function isMobileHexUx() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 1023px)').matches);
  }

  function atWidth(url, w) {
    var u = String(url || '');
    return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=' + w) : u;
  }
  function assetKey(url) {
    return String(url || '').split('?')[0].replace(/^https?:/, '');
  }
  function readCat(id) {
    var el = document.getElementById(id);
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  }
  function catalogueImages() {
    return readCat('df3d-catalogue-medium').concat(readCat('df3d-catalogue-large'));
  }
  function imagesForSrc(src) {
    var key = assetKey(src);
    if (!key) return [];
    var entries = catalogueImages();
    for (var i = 0; i < entries.length; i++) {
      var imgs = entries[i] && entries[i].images;
      if (!imgs || !imgs.length) continue;
      for (var j = 0; j < imgs.length; j++) {
        if (assetKey(imgs[j]) === key) return imgs;
      }
    }
    return [];
  }

  function showHexPhoto(stage, i) {
    var urls = [];
    try { urls = JSON.parse(stage.getAttribute('data-hex-urls') || '[]'); } catch (e) { urls = []; }
    if (!urls.length) return;
    var at = ((i % urls.length) + urls.length) % urls.length;
    stage.setAttribute('data-hex-at', String(at));
    var img = stage.querySelector('img');
    if (img) {
      img.setAttribute('src', atWidth(urls[at], 800));
      img.setAttribute('width', '800');
      img.setAttribute('height', '800');
    }
    var count = stage.querySelector('[data-hex-count]');
    if (count) count.textContent = (at + 1) + ' / ' + urls.length;
  }

  function stripNotes() {
    var tas = list.querySelectorAll('textarea[data-field="notes"]');
    for (var i = 0; i < tas.length; i++) {
      var block = tas[i].parentNode;
      if (block && block.style) block.style.display = 'none';
    }
  }

  function unwrapHexStages() {
    var stages = list.querySelectorAll('[data-hex-stage]');
    var i;
    for (i = 0; i < stages.length; i++) {
      var stage = stages[i];
      var img = stage.querySelector('img');
      var parent = stage.parentNode;
      if (img && parent) parent.insertBefore(img, stage);
      if (stage.parentNode) stage.parentNode.removeChild(stage);
    }
  }

  function enhanceHexCards() {
    stripNotes();
    if (!isMobileHexUx()) {
      unwrapHexStages();
      return;
    }
    var cards = list.querySelectorAll('[data-hex]');
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      if (card.querySelector('[data-hex-stage]')) continue;
      var img = card.querySelector('img');
      if (!img) continue;
      var src = img.getAttribute('src') || '';
      var urls = imagesForSrc(src);
      var next = atWidth(src, 800);
      if (next !== src) img.setAttribute('src', next);
      img.setAttribute('width', '800');
      img.setAttribute('height', '800');
      if (urls.length < 2) continue;

      var stage = document.createElement('div');
      stage.className = 'df3d-hex-stage';
      stage.setAttribute('data-hex-stage', '');
      stage.setAttribute('data-hex-at', '0');
      stage.setAttribute('data-hex-urls', JSON.stringify(urls));
      img.parentNode.insertBefore(stage, img);
      stage.appendChild(img);

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.setAttribute('data-hex-prev', '');
      prev.setAttribute('aria-label', 'Previous photo');
      prev.textContent = '\u2039';
      var nxt = document.createElement('button');
      nxt.type = 'button';
      nxt.setAttribute('data-hex-next', '');
      nxt.setAttribute('aria-label', 'Next photo');
      nxt.textContent = '\u203A';
      var count = document.createElement('span');
      count.setAttribute('data-hex-count', '');
      count.textContent = '1 / ' + urls.length;
      stage.appendChild(prev);
      stage.appendChild(nxt);
      stage.appendChild(count);
    }
  }

  list.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hex-prev], [data-hex-next]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      var stage = btn.closest('[data-hex-stage]');
      if (!stage) return;
      var at = parseInt(stage.getAttribute('data-hex-at'), 10) || 0;
      showHexPhoto(stage, btn.hasAttribute('data-hex-next') ? at + 1 : at - 1);
      return;
    }
    /* MOBILE-ONLY: keep photo browse in the strip. Do not let data-jumpgallery
     * scroll the left gallery into view after a card tap. */
    if (e.target.closest('[data-hex-stage], [data-hex] img')) {
      if (isMobileHexUx()) e.stopPropagation();
    }
  }, true);

  /* MOBILE-ONLY: center the new card so both neighbors peek. */
  function centerCardX(card) {
    if (!card || !isMobileHexUx()) return 0;
    var listW = list.clientWidth;
    var x = card.offsetLeft - (listW - card.offsetWidth) / 2;
    var max = Math.max(0, list.scrollWidth - listW);
    if (x < 0) x = 0;
    if (x > max) x = max;
    return x;
  }

  var keepX = 0;
  var lastCount = list.querySelectorAll('[data-hex]').length;
  list.addEventListener('scroll', function () {
    if (isMobileHexUx()) keepX = list.scrollLeft;
  }, { passive: true });

  enhanceHexCards();

  var mq = window.matchMedia && window.matchMedia('(max-width: 1023px)');
  function onHexMq() {
    keepX = 0;
    enhanceHexCards();
  }
  if (mq) {
    if (mq.addEventListener) mq.addEventListener('change', onHexMq);
    else if (mq.addListener) mq.addListener(onHexMq);
  }

  if (!window.MutationObserver) return;
  var obs = new MutationObserver(function () {
    enhanceHexCards();
    var cards = list.querySelectorAll('[data-hex]');
    var n = cards.length;
    if (!isMobileHexUx()) {
      lastCount = n;
      return;
    }
    if (n > lastCount && cards[n - 1]) {
      keepX = centerCardX(cards[n - 1]);
    }
    list.scrollLeft = keepX;
    lastCount = n;
  });
  obs.observe(list, { childList: true });
})();

/* OPEN: kill “Choose Your Edition” on Hex (not route / custom). All widths.
 * Circuit Hex now has Size+Color variants, so sellable_count > 1 re-opens the
 * old edition <select>. Size is per-hex chips. Leave a hidden name="id".
 */
(function () {
  'use strict';
  var cfg = document.getElementById('df3d-configurator');
  if (cfg && cfg.getAttribute('data-mode') === 'custom') return;

  function isEditionLabel(el) {
    var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return /^choose your edition$/i.test(t);
  }

  var labels = document.querySelectorAll('label');
  var i;
  for (i = 0; i < labels.length; i++) {
    if (!isEditionLabel(labels[i])) continue;
    var forId = labels[i].getAttribute('for');
    var sel = (forId && document.getElementById(forId)) || labels[i].nextElementSibling;
    if (sel && sel.tagName === 'SELECT' && sel.getAttribute('name') === 'id') {
      var hid = document.createElement('input');
      hid.type = 'hidden';
      hid.name = 'id';
      hid.value = sel.value || '';
      sel.parentNode.insertBefore(hid, sel);
      sel.remove();
    }
    var wrap = labels[i].parentNode;
    labels[i].remove();
    if (wrap && wrap !== document.body && !wrap.querySelector('input, select, textarea, button, [data-hex], [data-add-slot]')) {
      wrap.remove();
    }
  }

  if (cfg && cfg.getAttribute('data-mode') === 'preset') {
    var leftover = document.getElementById('variant-id');
    if (leftover && leftover.tagName === 'SELECT') {
      var h2 = document.createElement('input');
      h2.type = 'hidden';
      h2.name = leftover.getAttribute('name') || 'id';
      h2.value = leftover.value || '';
      leftover.parentNode.insertBefore(h2, leftover);
      leftover.remove();
      var lab = document.querySelector('label[for="variant-id"]');
      if (lab) lab.remove();
    }
  }
})();
